import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Calendar, Eye, BookOpen, Users, PenTool, PersonStanding, Edit, UserPen, Flag } from "lucide-react";

export interface ArticleCardProps {
  id: string;
  title: string;
  category: string;
  excerpt: string;
  imageUrl?: string;
  sub_category?: string;
  location?: string;
  eventDate?: string;
  writer?: string;
}

export const ArticleCard: React.FC<ArticleCardProps> = ({ 
  id, 
  title, 
  category, 
  excerpt, 
  imageUrl,
  sub_category,
  location,
  eventDate,
  writer
}) => {
  const articleUrl = `/articles/${id}`;
  const isInterview = category === 'INTERVIEW';
  const isArticles = category === 'ARTICLES';

  const getCategoryIcon = () => {
    switch (category) {
      case 'ARTICLES':
        return <BookOpen className="w-3 h-3 md:w-4 md:h-4" />;
      case 'INTERVIEW':
        return <Users className="w-3 h-3 md:w-4 md:h-4" />;
      case 'BLOG':
        return <PenTool className="w-3 h-3 md:w-4 md:h-4" />;
      default:
        return <Eye className="w-3 h-3 md:w-4 md:h-4" />;
    }
  };
  
  return (
    <Card className={`group overflow-hidden 
      bg-[rgba(8,20,20,0.55)] backdrop-blur-md 
      border border-[rgba(255,255,255,0.06)] 
      hover:bg-[rgba(8,20,20,0.62)] hover:border-[rgba(255,255,255,0.12)]
      transition-all duration-500
      ${isArticles ? 'flex flex-row gap-2 md:gap-4' : ''}`}
    >
      {imageUrl && (
        <div className={`relative ${isArticles ? 'w-1/3' : 'h-32 md:h-56'} overflow-hidden`}>
          <img 
            src={imageUrl} 
            alt={title} 
            className={`object-cover w-full h-full transition-transform duration-700 group-hover:scale-105
              `}
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        </div>
      )}
      <div className={`flex flex-col ${isArticles ? 'w-2/3' : ''}`}>
        <CardHeader className="space-y-2 md:space-y-3 p-3 md:p-6">
          <div className="flex items-center justify-between">
            <Badge 
              variant="outline" 
              className={`text-foreground/80 border-[rgba(255,255,255,0.12)] bg-white/5 hover:bg-white/10 tracking-wider flex items-center gap-1 md:gap-2 px-2 py-0.5 md:px-4 md:py-1 text-xs md:text-sm
                ${isArticles ? 'text-xs' : ''}`}
            >
              {getCategoryIcon()}
              {category}
            </Badge>
          </div>
          <Link to={articleUrl} className="block group">
            <CardTitle className={`font-medium text-foreground group-hover:text-foreground transition-colors duration-300 text-sm md:text-base
              
              ${isArticles ? 'text-sm' : ''}`}
            >
              {title}
            </CardTitle>
          </Link>
        </CardHeader>
        <CardContent className="space-y-2 md:space-y-4 px-3 md:px-6 py-0 md:py-2">
          <p className={`text-muted-foreground leading-relaxed hidden md:block
            
            ${isArticles ? 'text-sm line-clamp-4' : ''}`}
          >
            {excerpt}
          </p>
          
          <div className="flex flex-col space-y-1 md:space-y-2 text-xs md:text-sm text-elegant/70">
          {sub_category && (
              <div className="flex items-center gap-1 md:gap-2">
                <Flag className="w-3 h-3 md:w-4 md:h-4" />
                <span className="text-xs md:text-sm">{sub_category}</span>
              </div>
            )}
            {location && (
              <div className="flex items-center gap-1 md:gap-2">
                <MapPin className="w-3 h-3 md:w-4 md:h-4" />
                <span className="text-xs md:text-sm">{location}</span>
              </div>
            )}
            {eventDate && (
              <div className="flex items-center gap-1 md:gap-2">
                <Calendar className="w-3 h-3 md:w-4 md:h-4" />
                <span className="text-xs md:text-sm">{eventDate}</span>
              </div>
            )}
            {writer && (
              <div className="flex items-center gap-1 md:gap-2">
                <UserPen className="w-3 h-3 md:w-4 md:h-4" />
                <span className="text-xs md:text-sm">{writer}</span>
              </div>
            )}
          </div>
        </CardContent>
        <CardFooter className="flex justify-end mt-auto p-3 md:p-6">
          <Link
            to={articleUrl}
            className="text-foreground/70 hover:text-foreground transition-colors duration-300 flex items-center gap-1 md:gap-2 text-xs md:text-sm font-medium group"
          >
            詳しく見る
            <ArrowRight className="w-3 h-3 md:w-4 md:h-4 transform group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
        </CardFooter>
      </div>
    </Card>
  );
};